
import { IFormActions, IFormState, IFormSubscription, useFieldActions, useFieldState, useFormActions, useFormState } from "@ngt/forms-core";
import { useState, useEffect, useMemo, ReactNode, MouseEvent } from "react";
import { LoadingButton, LoadingButtonProps } from "@mui/lab";
import { FormError } from '../../errors';

export interface IFormActionButtonProps<T extends object = any> extends Omit<LoadingButtonProps, 'onClick' | 'loading'> {
    onClick: (event: MouseEvent<HTMLButtonElement>, formActions: IFormActions<T, FormError>) => Promise<void>;
    loading?: boolean | ((formState: IFormState<T, FormError>) => boolean);
    loadingSubscription?: Partial<IFormSubscription>;
}

const defaultSubscription = {};

const FormActionButton = <T extends object = any>({
    onClick,
    loading,
    loadingSubscription,
    ...rest
}: IFormActionButtonProps<T>) => {
    const [processing, setProcessing] = useState(false);

    const formActions = useFormActions<T, FormError>();
    const formState = useFormState<T, FormError>(loading instanceof Function ? loadingSubscription : defaultSubscription);

    const click = useMemo(() => {
        if (!onClick) {
            return;
        }

        return async (event: MouseEvent<HTMLButtonElement>) => {
            setProcessing(true);

            await onClick(event, formActions);

            setProcessing(false);
        };
    }, [onClick, formActions, setProcessing]);

    const loadingToUse = useMemo(() => {
        if (loading instanceof Function) {
            return loading(formState);
        }

        return loading;
    }, [loading, formState])

    return (
        <LoadingButton 
            {...rest}
            onClick={click}
            loading={loadingToUse || processing}
        />
    )
};

export default FormActionButton;